"use client";

import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getBuddyRequestTargetProfile } from "@/actions/buddy";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import type { ActivitySkill } from "@/db/schema/profile";
import { useActivityCatalog } from "@/hooks/use-activities";
import { createClientLogger } from "@/lib/logging/client";
import { BuddyRequestForm } from "./buddy-request-form";
import { BuddyRequestTrigger } from "./buddy-request-trigger";

const logger = createClientLogger(
  "components/profile/buddy-request/buddy-request-drawer",
);

type BuddyRequestProfile = {
  userId: string;
  fullName: string | null;
  activities: string[] | null;
  activitySkills: ActivitySkill[] | null;
};

export type BuddyRequestDrawerProps = {
  targetUserId: string;
  targetName: string | null;
  profile?: BuddyRequestProfile;
  isAlreadyBuddy?: boolean;
  chatId?: number;
  trigger?: React.ReactNode;
  onRequestSent?: () => void | Promise<void>;
};

export function BuddyRequestDrawer({
  targetUserId,
  targetName,
  profile,
  isAlreadyBuddy = false,
  chatId,
  trigger,
  onRequestSent,
}: BuddyRequestDrawerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [resolvedProfile, setResolvedProfile] =
    useState<BuddyRequestProfile | null>(profile ?? null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const { dictionary: activityDictionary, isLoading: isLoadingActivities } =
    useActivityCatalog();

  useEffect(() => {
    setResolvedProfile(profile ?? null);
    setProfileError(null);
  }, [profile]);

  useEffect(() => {
    if (!isOpen || resolvedProfile) {
      return;
    }

    let canceled = false;
    setIsLoadingProfile(true);
    setProfileError(null);

    void (async () => {
      try {
        const result = await getBuddyRequestTargetProfile({
          targetUserId,
        });

        if (canceled) {
          return;
        }

        if (!result.ok) {
          setProfileError(result.message ?? "Unable to load profile details");
          return;
        }

        setResolvedProfile(result.data);
      } catch (error) {
        if (canceled) {
          return;
        }
        logger.error("Failed to load buddy request target profile", error);
        setProfileError("Unable to load profile details");
      } finally {
        if (!canceled) {
          setIsLoadingProfile(false);
        }
      }
    })();

    return () => {
      canceled = true;
    };
  }, [isOpen, resolvedProfile, targetUserId]);

  const enrichedActivities = useMemo(() => {
    const selectedActivities = resolvedProfile?.activities ?? [];
    const activitySkills = resolvedProfile?.activitySkills ?? [];

    return selectedActivities
      .map((activityId) => {
        const activity = activityDictionary.get(activityId);
        const skill = activitySkills.find(
          (entry) => entry.activityId === activityId,
        );
        if (!activity) {
          return null;
        }
        const level = activity.levels.find(
          (entry) => entry.score === skill?.skillLevel,
        );
        return { activity, skill, level };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  }, [
    activityDictionary,
    resolvedProfile?.activities,
    resolvedProfile?.activitySkills,
  ]);

  const defaultTrigger = (
    <BuddyRequestTrigger
      isAlreadyBuddy={isAlreadyBuddy}
      chatId={chatId}
      targetUserId={targetUserId}
    />
  );

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen} modal>
      <DrawerTrigger asChild>{trigger ?? defaultTrigger}</DrawerTrigger>
      <DrawerContent className="h-[85dvh] max-h-[85dvh] overflow-hidden border-t border-border outline-none">
        <DrawerTitle className="sr-only">Buddy Request</DrawerTitle>
        <div className="relative mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col overflow-hidden px-4 pt-4 pb-[max(env(safe-area-inset-bottom),1.5rem)]">
          <div className="mb-6 shrink-0 text-center">
            <h2 className="bg-gradient-to-r from-brand to-purple-600 bg-clip-text text-xl font-bold text-transparent">
              Connect with {targetName?.split(" ")[0] || "them"}
            </h2>
            <p className="mt-1 text-sm text-text-secondary">
              Send a request to start chatting or plan an activity together.
            </p>
          </div>

          {isOpen && isLoadingProfile && (
            <div className="flex h-full flex-1 items-center justify-center text-sm text-text-secondary">
              <Loader2 className="mr-2 size-4 animate-spin" />
              Loading profile details...
            </div>
          )}

          {isOpen && !isLoadingProfile && profileError && (
            <div className="flex flex-1 items-center justify-center rounded-2xl border border-border bg-surface-alt/50 p-4 text-center text-sm text-text-secondary">
              {profileError}
            </div>
          )}

          {isOpen && !isLoadingProfile && !profileError && resolvedProfile && (
            <BuddyRequestForm
              key={isOpen ? "open" : "closed"}
              targetUserId={targetUserId}
              enrichedActivities={enrichedActivities}
              isLoadingActivities={isLoadingActivities}
              onSuccess={() => {
                setIsOpen(false);
                void onRequestSent?.();
              }}
            />
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
